import { isArray, isEmpty, isEqual, isNull, isUndefined } from 'lodash/fp';
import Lottie from 'lottie-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ANALYSIS_VIEW_BUTTONS } from '../../../hooks/useSplitView';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { ClientFeaturePermission, FeaturePermission } from '../../admin/users/store';
import { getClientHasFeaturePermission, getUser, getUserHasFeaturePermission } from '../../auth/login/store';
import { UserRole } from '../../constants/permittedRoles';
import { MLDataValue, MLObject } from '../../datasets/store/mlTypes';
import { askChatbotStarted, getAIAnswer, getAIResults, getChatbotLoading, getChatbotModalOpen, initialAIAnswer, resetChatbotModal, toggleChatbotModal } from '../../documents/chatbot/store';
import { clearPdfSearchTerm, getPdfSearchTerm, setSearchResults } from '../../documents/document-viewer/store';
import { ButtonResize } from '../button/ButtonResize';
import { Delete, Ellipsis, QuestionMark, Sort } from '../icons';
import { Chatbot } from '../icons/Chatbot';
import { LongText } from '../longtext/LongText';
import { DraggablePositionModal } from '../modal/DraggablePositionModal';
import { ModalHeader } from '../modal/ModalHeader';
import { Scrollable } from '../scrollable/Scrollable';
import { OverflowTooltip } from '../tooltip';
import { Typewriter } from '../typewriter/Typewriter';
import styles from './CustomDocumentQuery.module.scss';
import { getLottieAnimation, LottieAntimationTypes } from '../../../assets/lottie/getLottieAnimation';

const { primary, white, amber, red } = styles;

const PAGE_MARGIN = 20;
const BUTTON_WIDTH = 150;

export const CustomDocumentQuery: React.FC = () => {
    const [question, setQuestion] = useState<string>('');
    const [buttonTimeout, setButtonTimeout] = useState<number>(0);
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getChatbotModalOpen);
    const questionLoading = useAppSelector(getChatbotLoading);
    const aiAnswer = useAppSelector(getAIAnswer);
    const pdfSearchTerm = useAppSelector(getPdfSearchTerm);
    const aiResults = useAppSelector(getAIResults);
    const user = useAppSelector(getUser);

    const { value, page } = aiAnswer;

    const userIsBlocked = useMemo(() => user?.chatBotBlocked === 1, [user]);
    const clientHasFeaturePermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.CUSTOM_QUERYING_DOCUMENTS_WITH_AI]));
    const userHasFeaturePermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.CUSTOM_QUERYING_DOCUMENTS_WITH_AI]));

    const disabledMessage = useMemo(() => {
        let disabledMessage: null | { message: string, colour: string } = null;
        if (userIsBlocked) {
            disabledMessage = { message: `You are currently blocked from accessing the chat bot due to a number of questions that have failed our suitability checks, please speak to ${user?.role === UserRole.ADMIN ? 'a member of the ARK-51 team' : 'your administrator'}.`, colour: red };
        }
        if (!clientHasFeaturePermission) {
            disabledMessage = { message: `You do not currently have permission to access this feature, please speak to ${user?.role === UserRole.ADMIN ? 'a member of the ARK-51 team to upgrade' : 'your administrator'}.`, colour: amber };
        }
        if (clientHasFeaturePermission && !userHasFeaturePermission) {
            disabledMessage = { message: 'You do not currently have permission to access this feature, please speak to your administrator.', colour: amber };
        }
        return disabledMessage;
    }, [userIsBlocked, clientHasFeaturePermission, userHasFeaturePermission, user]);

    const [screenWidth] = useWindowResize();
    const modalWidth = useMemo(() => ((screenWidth - ANALYSIS_VIEW_BUTTONS) / 2) - PAGE_MARGIN, [screenWidth]);
    const initialOffset = useMemo(() => modalWidth + ANALYSIS_VIEW_BUTTONS + ((3 * PAGE_MARGIN) / 2), [modalWidth]);

    const resetQuery = useCallback((scrollToTop = false) => {
        dispatch(resetChatbotModal());
        dispatch(clearPdfSearchTerm());
        setQuestion('');
        if (scrollToTop) {
            dispatch(setSearchResults([{ page: 1, indexes: [] }]));
        }
    }, [dispatch]);

    const submit = useCallback(() => {
        dispatch(askChatbotStarted(question));
        if (pdfSearchTerm !== '') {
            dispatch(clearPdfSearchTerm());
        }
        setButtonTimeout(30);
    }, [dispatch, question, pdfSearchTerm]);

    const onKeyDownSubmit = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key.toLowerCase() === 'enter' && buttonTimeout <= 0) {
            submit();
        }
    }, [submit, buttonTimeout]);

    const updateQuestion = useCallback((value: string) => {
        setQuestion(value);
        if (value === '') {
            resetQuery();
        }
    }, [resetQuery]);

    const closeModal = useCallback(() => {
        dispatch((toggleChatbotModal(false)));
        resetQuery();
    }, [dispatch, resetQuery]);

    useEffect(() => {
        if (buttonTimeout > 0) {
            setTimeout(() => {
                setButtonTimeout(buttonTimeout - 1);
            }, 1000);
        }
    }, [buttonTimeout]);

    const showReset = useMemo(() => question.length > 0 || !isEqual(aiAnswer, initialAIAnswer), [question, aiAnswer]);
    const showScrollToAnswer = useMemo(() => aiResults.page !== 0 && !questionLoading && !isUndefined(page) && isNull(disabledMessage), [aiResults, questionLoading, page, disabledMessage]);

    const showIconButtons = useMemo(() => {
        const displayedButtonsWidth = [showReset, showScrollToAnswer].reduce((totalButtonWidth, shouldIncludeButton) => shouldIncludeButton ? totalButtonWidth + BUTTON_WIDTH : totalButtonWidth, 2 * BUTTON_WIDTH);
        return displayedButtonsWidth >= modalWidth;
    }, [showReset, showScrollToAnswer, modalWidth]);

    const scrollToAnswer = useCallback(() => {
        if (pdfSearchTerm !== '') {
            dispatch(clearPdfSearchTerm());
        }
        dispatch(setSearchResults([aiResults]));
    }, [dispatch, aiResults, pdfSearchTerm]);

    const askButtonDisabled = useMemo(() => questionLoading || question.length === 0 || buttonTimeout > 0 || !isNull(disabledMessage), [questionLoading, question, buttonTimeout, disabledMessage]);
    const scrollButtonDisabled = useMemo(() => aiResults.page === 0 || questionLoading || isUndefined(page) || !isNull(disabledMessage), [aiResults, questionLoading, page, disabledMessage]);

    const buttonTimeoutLabel = useMemo(() => buttonTimeout > 0 ? `Ask again: ${buttonTimeout}s` : 'Ask', [buttonTimeout]);

    const mlDataValueToString = useCallback((value: MLDataValue) => {
        let textValue = '';
        if (typeof value === 'boolean') {
            textValue = value ? 'Yes' : 'No';
        }
        if (['string', 'number'].includes(typeof value)) {
            textValue = typeof value === 'string' ? value : (value as number).toString();
        }
        if (isArray(value)) {
            const isStringArray = typeof value[0] === 'string';
            if (isStringArray) {
                textValue = value.join(', ');
            }
        }
        return textValue;
    }, []);

    const typewriterTextValue = useMemo(() => {
        const returnNotFound = isNull(value) || isEmpty(value) || isArray(value) && isEmpty(value[0]);

        if (returnNotFound) {
            const textValue = 'AI did not find a suitable response for this question.';
            return <Typewriter text={textValue} />;
        }

        if (['string', 'number', 'boolean'].includes(typeof value)) {
            const textValue = mlDataValueToString(value);
            return <Typewriter text={textValue} />;
        }

        if (isArray(value)) {
            const isStringArray = typeof value[0] === 'string';
            if (isStringArray) {
                return <Typewriter text={value.join(', ')} />;
            }

            return (
                <>
                    {(value as MLObject[]).map((row, i) => (
                        <div key={i}>
                            {(Object.entries(row)).map(([key, value]) => {
                                const textValue = mlDataValueToString(value);
                                return (
                                    <Typewriter key={key} text={textValue} />
                                );
                            })}
                        </div>
                    ))}
                </>
            );
        }

        return (
            <>
                {(Object.entries(value as MLObject)).map(([key, value]) => {
                    const textValue = mlDataValueToString(value);
                    return (
                        <Typewriter key={key} text={textValue} />
                    );
                })}
            </>
        );
    }, [value, mlDataValueToString]);

    const animation = useMemo(() => getLottieAnimation(LottieAntimationTypes.ANIMATION), []);

    const answerComponent = useMemo(() => {
        if (questionLoading) {
            return (
                <div className={styles.spinnerWrapper}>
                    <div className={styles.spinner}>
                        <Lottie animationData={animation} loop />
                    </div>
                </div>);
        }
        if (!isNull(disabledMessage)) {
            return (
                <div className={styles.disabledMessageWrapper}>
                    <div className={styles.disabledMessage} style={{ color: disabledMessage.colour }}>{disabledMessage.message}</div>
                </div>
            );
        }
        if (value === '') {
            return null;
        }
        return (
            <div className={styles.aiAnswerWrapper}>
                <div className={styles.aiAnswer}>
                    <Scrollable height='100%' maxHeight='100%'>
                        {typewriterTextValue}
                    </Scrollable>
                </div>
            </div>
        );
    }, [questionLoading, value, typewriterTextValue, disabledMessage, animation]);

    useEffect(() => () => {
        closeModal();
    }, [closeModal]);

    return (
        <DraggablePositionModal
            closeModal={closeModal}
            isOpen={isOpen}
            initialPosition={{ x: initialOffset, y: 170 }}
            testId='chat-bot-draggable-modal'
            height='500px'
            width={`${modalWidth}px`}
            shouldRepositionOnResize
        >
            <div className={styles.aiQuestionModalWrapper}>
                <div className={styles.aiQuestionAndDescriptionWrapper}>
                    <ModalHeader icon={Chatbot} label='Ask a question' iconColor={primary} iconFill={white} />
                    <div className={styles.aiQuestionModalContent}>
                        <div className={styles.aiQuestionModalDescriptionWrapper}>
                            <div className={styles.aiQuestionModalDescription}>
                                <OverflowTooltip overlayText='If you have a question to ask about the document you are currently viewing, you can do it here.' />
                            </div>
                            <div className={styles.aiQuestionModalDescription}>
                                <OverflowTooltip overlayText='Please note, the AI will not remember previous questions, so each question should be asked independently.' />
                            </div>
                        </div>
                        <LongText
                            testId='chat-bot-text-area'
                            placeholder='Ask me anything!'
                            maxLength={2000}
                            value={question}
                            onChange={value => updateQuestion(value)}
                            width='100%'
                            onKeyDown={onKeyDownSubmit}
                            disabled={!isNull(disabledMessage)}
                        />
                    </div>
                </div>
                <div className={styles.aiQuestionAndAnswerWrapper}>
                    {answerComponent}
                </div>
            </div>
            <div className={styles.buttonWrapper}>
                <div className={styles.leftButtons}>
                    <ButtonResize showIconButton={showIconButtons} label='Close' onClick={closeModal} icon={Delete} />
                    {showReset && <ButtonResize showIconButton={showIconButtons} label='Reset' onClick={() => resetQuery(true)} icon={Ellipsis} />}
                </div>
                <div className={styles.rightButtons}>
                    {showScrollToAnswer && <ButtonResize showIconButton={showIconButtons} label='Scroll to answer' onClick={scrollToAnswer} disabled={scrollButtonDisabled} icon={Sort} />}
                    <ButtonResize showIconButton={showIconButtons} label={buttonTimeoutLabel} onClick={submit} disabled={askButtonDisabled} icon={QuestionMark} showSpinner={questionLoading} />
                </div>
            </div>
        </DraggablePositionModal>
    );
};
